import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouseUser } from "@fortawesome/free-solid-svg-icons";
import Format from "../../../helpers/Format";
import { Inscription } from "../../../services/ConsultarService";
import Modal from "../../Modal";
import Show from "../../Show";
import Button from "../../Button";

import style from "./styles.module.scss";

const cn = (...classNames: string[]) => classNames
  .join(" ")
  .split(/\s+/)
  .filter(Boolean)
  .map((className) => style[className] || className)
  .join(" ");

interface Props {
  inscriptions: Inscription[];
  attendantCounter: boolean;
  onSelect(inscription: Inscription): void;
  onSelectAll(inscriptions: Inscription[], selected: boolean): void;
  onPix?(inscription: Inscription): void;
  onBillet?(inscription: Inscription): void;
  individualActionsDisabled?: boolean;
}

const keyI18n = "translations.consultDebts.inscriptionItem";
const phaseTranslationKeys: Record<string, string> = {
  protested: "protested",
  legal_action: "legalAction",
};

export default function ItemInscricaoV2({
  inscriptions,
  attendantCounter,
  onSelect,
  onSelectAll,
  onPix,
  onBillet,
  individualActionsDisabled = false,
}: Props) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [registryInscription, setRegistryInscription] = useState<Inscription>();
  const firstInscription = inscriptions[0];

  if (!firstInscription) return null;

  const selectableInscriptions = inscriptions.filter(({ group_payment, balance }) => (
    Boolean(group_payment) && balance !== 0
  ));
  const allSelected = selectableInscriptions.length > 0 &&
    selectableInscriptions.every(({ selected }) => Boolean(selected));
  const totalBalance = inscriptions.reduce((total, { balance }) => total + balance, 0);
  const fullAddress = firstInscription.full_address && [
    firstInscription.full_address.street,
    firstInscription.full_address.street_number,
    firstInscription.full_address.neighborhood,
    firstInscription.full_address.city,
    firstInscription.full_address.state,
    firstInscription.full_address.cep,
  ].filter(Boolean).join(", ");

  return (
    <div className={cn("item-inscricao item-inscricao-v2")}>
      <div className={cn("item-inscricao-actions")}>
        <div className={cn("option")}>
          <div className={cn("inscriptionInformation")}>
            <Button
              variant="text"
              iconButton
              size="extra-small"
              type="button"
              className={cn("administrativeToggleButton")}
              aria-label={t(`${keyI18n}.aria.${isExpanded ? "closeAdministrative" : "openAdministrative"}`, {
                number: firstInscription.administrative_inscription,
              })}
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded((expanded) => !expanded)}
              icon={(
                <span className="material-symbols-sharp" aria-hidden="true">
                  {isExpanded ? "arrow_drop_down" : "arrow_right"}
                </span>
              )}
            />

            <strong className={cn("administrativeTitle")}>
              <span className={cn("administrativeInscription")}>
                {t(`${keyI18n}.labels.administrativeInscription`)} {firstInscription.administrative_inscription}
              </span>
              <span>{t(`${keyI18n}.labels.cdaCount`, { count: inscriptions.length })}</span>
              <Show if={attendantCounter && firstInscription.receipt_type_id === 4}>
                <span className={cn("thirdColumn")} data-title={fullAddress}>
                  <FontAwesomeIcon icon={faHouseUser} />
                  <strong> {t(`${keyI18n}.labels.address`)}</strong>
                </span>
              </Show>
            </strong>

            <div className={cn("administrativeSelection")}>
              <span className={cn("administrativeTotal")}>
                <strong>{t(`${keyI18n}.labels.subtotal`)}</strong>{" "}
                <span className={cn("monetaryValue")}>{Format.currency(totalBalance)}</span>
              </span>
              <label className={cn(
                "checkboxControl",
                selectableInscriptions.length === 0 ? "checkboxControlDisabled" : "",
              )}>
                <input
                  type="checkbox"
                  aria-label={t(`${keyI18n}.aria.selectAll`, {
                    number: firstInscription.administrative_inscription,
                  })}
                  checked={allSelected}
                  disabled={selectableInscriptions.length === 0}
                  onChange={(event) => onSelectAll(selectableInscriptions, event.currentTarget.checked)}
                />
              </label>
            </div>
          </div>

          {isExpanded && inscriptions.map((inscription, inscriptionIndex) => {
            const debt = inscription.debts[0];
            const registry = debt?.protests_info?.[0];
            const phases = inscription.phases as string[];
            const canNegotiate = Boolean(inscription.group_payment) && inscription.balance !== 0;
            const paymentActionsDisabled = individualActionsDisabled || !canNegotiate;
            const canPayWithPix = !paymentActionsDisabled && Boolean(onPix);
            const canGenerateBillet = !paymentActionsDisabled && Boolean(onBillet);

            return (
              <div
                className={cn(
                  "inscriptionContent",
                  inscriptionIndex === inscriptions.length - 1 ? "lastCda" : "",
                )}
                key={inscription.id}
              >
                <div className={cn("individualPaymentActions")}>
                  <Button
                    variant="outlined"
                    iconButton
                    type="button"
                    className={cn("individualActionButton")}
                    aria-label={t(`${keyI18n}.aria.payPix`, { cda: inscription.cda })}
                    tooltip={canPayWithPix ? t(`${keyI18n}.actions.payPix`) : undefined}
                    tooltipPlacement="bottom"
                    disabled={!canPayWithPix}
                    onClick={onPix ? () => onPix(inscription) : undefined}
                    icon={<span className="material-symbols-sharp" aria-hidden="true">qr_code_2</span>}
                  />
                  <Button
                    variant="outlined"
                    iconButton
                    type="button"
                    className={cn("individualActionButton")}
                    aria-label={t(`${keyI18n}.aria.generateBillet`, { cda: inscription.cda })}
                    tooltip={canGenerateBillet ? t(`${keyI18n}.actions.generateBillet`) : undefined}
                    tooltipPlacement="bottom"
                    disabled={!canGenerateBillet}
                    onClick={onBillet ? () => onBillet(inscription) : undefined}
                    icon={<span className="material-symbols-sharp" aria-hidden="true">barcode</span>}
                  />
                </div>

                <div className={cn("cdaDetails")}>
                  <div className={cn("cdaInformationRow")}>
                    {inscription.cda && (
                      <span className={cn("cdaNumber")}>
                        <strong className={cn("labelInscription")}>{t(`${keyI18n}.labels.cda`)} </strong>{inscription.cda}
                      </span>
                    )}
                    {inscription.judicial_process_number && (
                      <span className={cn("cdaProcess")}>
                        <strong className={cn("labelInscription")}>{t(`${keyI18n}.labels.process`)} </strong>
                        <span className={cn("processNumber bodyMediumValue")}>
                          {inscription.judicial_process_number}
                        </span>
                      </span>
                    )}
                    {(inscription.state_pt_br || phases.length > 0) && (
                      <span className={cn("cdaPhase")}>
                        <strong className={cn("visuallyHidden")}>
                          {t(`${keyI18n}.labels.phase`)}{" "}
                        </strong>
                        {inscription.state_pt_br && (
                          <span className={cn("phaseName")}><strong>{inscription.state_pt_br}</strong></span>
                        )}
                        {phases.map((phase) => {
                          const phaseKey = phaseTranslationKeys[phase];
                          const label = phaseKey ? t(`${keyI18n}.phases.${phaseKey}`) : "";
                          return label ? (
                            <span className={cn("phaseName")} key={phase}><strong>{label}</strong></span>
                          ) : null;
                        })}
                      </span>
                    )}
                  </div>

                  <div className={cn("cdaInformationRow")}>
                    {debt?.fiscal_date && (
                      <span className={cn("cdaExercise")}>
                        <strong className={cn("labelInscription")}>{t(`${keyI18n}.labels.exercise`)} </strong>
                        <span className={cn("bodyMediumValue")}>{Format.fullDate(debt.fiscal_date)}</span>
                      </span>
                    )}
                    {(inscription.state_pt_br === "Protestado" ||
                      inscription.state_pt_br === "Encaminhada para protesto" ||
                      phases.includes("protested")) && registry?.registry_name && (
                      <span className={cn("cdaRegistry")}>
                        <strong className={cn("labelInscription")}>{t(`${keyI18n}.labels.registry`)} </strong>
                        <span className={cn("bodyMediumValue")}>{registry.registry_name}</span>{" "}
                        <Button
                          variant="text"
                          size="extra-small"
                          type="button"
                          className={cn("addressModal")}
                          onClick={() => setRegistryInscription(inscription)}
                        >
                          {t(`${keyI18n}.actions.viewAddress`)}
                        </Button>
                      </span>
                    )}
                  </div>
                </div>

                <div className={cn("cdaSelection")}>
                  <span className={cn("cdaBalance")}>
                    <strong>
                      {t(`${keyI18n}.labels.${inscription.refis_enabled ? "refisCdaTotal" : "cdaTotal"}`)}
                    </strong>
                    <span className={cn("monetaryValue")}>{Format.currency(inscription.balance)}</span>
                    {inscription.refis_enabled && (
                      <span className={cn("withoutRefisValue")}>{Format.currency(inscription.balance_to_legal_action)}</span>
                    )}
                  </span>
                  <label className={cn(
                    "checkboxControl",
                    !canNegotiate ? "checkboxControlDisabled" : "",
                  )}>
                    <input
                      type="checkbox"
                      aria-label={t(`${keyI18n}.aria.selectCda`, { cda: inscription.cda })}
                      checked={canNegotiate && Boolean(inscription.selected)}
                      disabled={!canNegotiate}
                      onChange={() => onSelect(inscription)}
                    />
                  </label>
                </div>
              </div>
            );
          })}

          <Modal
            show={Boolean(registryInscription)}
            onHide={() => setRegistryInscription(undefined)}
            centered={true}
          >
            <div className={cn("modalContentBody")}>
              <div className={cn("modalTittle")}><span className="pgm-title-medium">{t(`${keyI18n}.registryModal.title`)}</span></div>
              <div className={cn("modalContent")}>
                <span className="pgm-body-large"><strong className={cn("labelInscription")}>{t(`${keyI18n}.labels.registry`)} </strong>{registryInscription?.debts[0]?.protests_info?.[0]?.registry_name}</span>
                <span className="pgm-body-large"><strong className={cn("labelInscription")}>{t(`${keyI18n}.labels.address`)}: </strong>{registryInscription?.debts[0]?.protests_info?.[0]?.full_address}</span>
                <span className="pgm-body-large"><strong className={cn("labelInscription")}>{t(`${keyI18n}.labels.phone`)} </strong>{registryInscription?.debts[0]?.protests_info?.[0]?.registry_phone}</span>
              </div>
            </div>
          </Modal>
        </div>
      </div>
    </div>
  );
}
